// iter624 — Live Insight Toast.
// Listens for twin update events globally and surfaces a 7s toast explaining
// WHY the composite changed (TSH delta, axis activation, hereditary risk, etc.)
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/App";

export default function LiveInsightToast() {
  const { API } = useAuth();
  const lastCompositeRef = useRef(null);
  const lastAxisRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const fetchAndCompare = async (eventName) => {
      try {
        const [tsRes, axisRes] = await Promise.all([
          axios.get(`${API}/health-twin/twin-score?days=30`).catch(() => null),
          axios.get(`${API}/stress-twin/cortisol-thyroid-axis`).catch(() => null),
        ]);
        if (cancelled) return;
        const newComposite = tsRes?.data?.composite;
        const axisDetected = !!axisRes?.data?.detected;
        const prev = lastCompositeRef.current;
        const prevAxis = lastAxisRef.current;

        // Initial load — store baseline, no toast
        if (prev === null && newComposite != null) {
          lastCompositeRef.current = newComposite;
          lastAxisRef.current = axisDetected;
          return;
        }

        const messages = [];
        // Composite change
        if (prev != null && newComposite != null && Math.abs(newComposite - prev) >= 2) {
          const diff = newComposite - prev;
          const arrow = diff > 0 ? "↗" : "↘";
          const verb = diff > 0 ? "се подобри" : "намаля";
          messages.push(`${arrow} Twin Score ${verb} от ${prev} → ${newComposite}`);
        }
        // Axis activation/deactivation
        if (axisDetected !== prevAxis) {
          if (axisDetected) {
            messages.push("🧬 Cortisol-Thyroid axis сега е активен — провери Stress страницата.");
          } else if (prevAxis) {
            messages.push("✅ Cortisol-Thyroid axis вече не е активен — TSH или кортизолът се нормализира.");
          }
        }
        // Event-specific cue
        if (messages.length === 0) {
          if (eventName === "hereditary-risk-changed") {
            messages.push("🛡 Наследственият риск е обновен — провери препоръките.");
          }
        }

        if (messages.length > 0) {
          // iter1004 — bottom-left: no header widgets there, no collision with
          // the bottom-right AI FAB. Solid gradient stays.
          // iter1006 — closeButton + dismissible so users who read slowly
          // can dismiss the toast on demand instead of waiting the full duration.
          toast(messages.join(" "), {
            duration: 7000,
            position: "bottom-left",
            className: "live-insight-toast",
            closeButton: true,
            dismissible: true,
            style: {
              background: "linear-gradient(135deg, #f0fdfa 0%, #ecfeff 100%)",
              color: "#0f172a",
              border: "1px solid #99f6e4",
              boxShadow: "0 8px 24px -6px rgba(15,23,42,0.20), 0 4px 8px -2px rgba(15,23,42,0.08)",
              backdropFilter: "none",
              WebkitBackdropFilter: "none",
              opacity: 1,
              fontWeight: 600,
            },
          });
        }
        lastCompositeRef.current = newComposite ?? prev;
        lastAxisRef.current = axisDetected;
      } catch { /* silent */ }
    };

    // Initial fetch to set baseline
    fetchAndCompare("init");

    const events = ["thyroid-lab-changed", "stress-log-changed", "hereditary-risk-changed", "meal-log-changed"];
    const handlers = {};
    events.forEach((e) => {
      handlers[e] = () => setTimeout(() => fetchAndCompare(e), 800);
      window.addEventListener(e, handlers[e]);
    });
    return () => {
      cancelled = true;
      events.forEach((e) => window.removeEventListener(e, handlers[e]));
    };
  }, [API]);

  return null;
}
